import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';

const CustomCursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  const hideCursor = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    // Detect touch device
    const checkTouchDevice = () => {
      const hasTouchScreen = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0
      );
      setIsTouchDevice(hasTouchScreen);
    };
    
    checkTouchDevice();
  }, []);

  useEffect(() => {
    if (isTouchDevice) {
      // Touch device: show cursor on tap, hide on scroll
      const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setMousePosition({ x: touch.clientX, y: touch.clientY });
        setIsVisible(true);
      };

      const handleTouchMove = () => {
        // Hide cursor when scrolling/moving on touch
        setIsVisible(false);
      };

      const handleScroll = () => {
        // Hide cursor when scrolling
        setIsVisible(false);
      };

      window.addEventListener('touchstart', handleTouchStart);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('scroll', handleScroll);
      };
    } else {
      // Desktop: normal mouse behavior
      const updateMousePosition = (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
        setIsVisible(true);
      };

      const handleMouseOver = (e) => {
        if (e.target.closest('a, button, [role="button"]')) {
          setIsHovering(true);
        }
      };

      const handleMouseOut = (e) => {
        if (e.target.closest('a, button, [role="button"]')) {
          setIsHovering(false);
        }
      };

      const handleMouseLeave = () => {
        setIsVisible(false);
      };

      window.addEventListener('mousemove', updateMousePosition);
      document.addEventListener('mouseover', handleMouseOver);
      document.addEventListener('mouseout', handleMouseOut);
      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        window.removeEventListener('mousemove', updateMousePosition);
        document.removeEventListener('mouseover', handleMouseOver);
        document.removeEventListener('mouseout', handleMouseOut);
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, [isTouchDevice]);

  return (
    <>
      {/* Main cursor dot with glow */}
      <motion.div
        className="fixed w-3 h-3 rounded-full pointer-events-none z-[9999]"
        style={{
          background: 'linear-gradient(135deg, #FFD700, #FFEC8B, #FFD700)',
          boxShadow: '0 0 10px #FFD700, 0 0 20px rgba(255, 215, 0, 0.5)'
        }}
        animate={{
          x: mousePosition.x - 6,
          y: mousePosition.y - 6,
          scale: isHovering ? 0 : 1,
          opacity: isVisible ? 1 : 0
        }}
        transition={{ 
          type: 'spring', 
          stiffness: 500, 
          damping: 28,
          opacity: { duration: 0.2 }
        }}
      />
      
      {/* Outer ring with gold glow */}
      <motion.div
        className="fixed w-12 h-12 rounded-full pointer-events-none z-[9998]"
        style={{
          border: '1px solid rgba(255, 215, 0, 0.6)',
          boxShadow: isHovering 
            ? '0 0 20px rgba(255, 215, 0, 0.8), 0 0 40px rgba(255, 215, 0, 0.4)' 
            : '0 0 10px rgba(255, 215, 0, 0.3)'
        }}
        animate={{
          x: mousePosition.x - 24,
          y: mousePosition.y - 24,
          scale: isHovering ? 2 : 1,
          opacity: isVisible ? 1 : 0
        }}
        transition={{ 
          type: 'spring', 
          stiffness: 150, 
          damping: 15,
          opacity: { duration: 0.2 }
        }}
      />
    </>
  );
};

export default CustomCursor;
